import { ampli } from 'lib-shared-tracking';
import type {
  SearchFilterCondition,
  SearchFilterValue,
} from 'lib-shared-types-api';
import { useCallback, useMemo } from 'react';


import {
  FilterItemType,
  staticFilterFields,
} from '../../../../../../../utils/definitions';
import { ConditionFieldProps } from '../../../conditionField';

type UseConditionFieldSelectOptions = {
  condition: SearchFilterCondition;
  onChange: ConditionFieldProps['onChange'];
};

type UseConditionFieldSelectResult = {
  handleClear: VoidFunction;
  handleChange: (item: FilterItemType) => void;
  mapValuesToItems: () => FilterItemType[];
};

export function useConditionFieldSelect({
  condition,
  onChange,
}: UseConditionFieldSelectOptions): UseConditionFieldSelectResult {
  const trackValue = useMemo(
    () => staticFilterFields.includes(condition.field),
    [condition.field],
  );

  const handleClear = useCallback(() => {
    ampli.searchFilterValueCleared({
      type: condition.field,
      count: condition.values.length,
    });

    onChange([]);
  }, [condition.field, condition.values.length, onChange]);

  const handleChange = useCallback(
    (item: FilterItemType) => {
      const { name: label, id: value } = item;
      const nextValues = [...condition.values];
      const isNew = !condition.values.some(({ value }) => value === item.id);

      if (isNew) {
        const nextValue: SearchFilterValue = { label, value: value.toString() };
        nextValues.push(nextValue);

        ampli.searchFilterValueAdded({
          type: condition.field,
          operator: condition.operator,
          value: trackValue ? nextValue.value : undefined,
        });
      } else {
        nextValues.splice(
          nextValues.findIndex((i) => i.value === value),
          1,
        );
      }

      onChange(nextValues);
    },
    [condition, trackValue, onChange],
  );

  const mapValuesToItems = useCallback(() => {
    return condition.values.map(({ label: name, value: id }) => ({
      id,
      name,
    }));
  }, [condition]);

  return { handleChange, handleClear, mapValuesToItems };
}
